import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  forwardRef,
  inject,
} from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { Country } from 'src/app/services/countries/countries.service';
import {
  CompanySearchAutocompleteItem,
  CompanySearchService,
} from 'src/app/shared/company-search-service/company-search.service';
import { PartyType, RepresentativeType } from 'src/types';
import {
  EditCaseDialogService,
  RepresentativeControls,
} from '../edit-case-dialog.service';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { AddressFormComponent } from '../address-form/address-form.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { RepresentativesFormComponent } from '../representatives-form/representatives-form.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-representative-form',
  templateUrl: './representative-form.component.html',
  styleUrls: ['./representative-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeAutocompleteComponent,
    AddressFormComponent,
    PayeeButtonComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    forwardRef(() => RepresentativesFormComponent),
    DatePipe,
  ],
})
export class RepresentativeFormComponent
  implements OnInit, OnDestroy
{
  private companySearchService = inject(CompanySearchService);
  private editCaseDialogService = inject(EditCaseDialogService);

  private readonly destroy = new Subject<void>();
  private readonly companySearch$ = new Subject<string>();

  @Input({ required: true }) is_standalone!: boolean;
  @Input({ required: true }) index!: string;
  @Input({ required: true }) representative!: FormGroup<RepresentativeControls>;
  @Input({ required: true }) representativeTypes!: RepresentativeType[];
  @Input({ required: true }) countries!: Country[];

  autocompleteOptions: CompanySearchAutocompleteItem[] = [];

  get children() {
    return this.representative.controls.representatives;
  }

  get registrationOfficeOptions(): number[] {
    return this.editCaseDialogService.registrationOffices.map((office) =>
      Number(office.id),
    );
  }

  get representativeTypeOptions(): number[] {
    return this.representativeTypes.map((rt) => rt.id);
  }

  get partyTypes(): PartyType[] {
    return this.editCaseDialogService.partyTypes;
  }

  get partyTypeOptions(): number[] {
    return this.partyTypes.map((partyType) => partyType.id);
  }

  get party_type_id() {
    return this.representative.controls.party_type_id;
  }

  isHouse(): boolean {
    return (
      this.partyTypes.find(
        (partyType) => partyType.id === this.representative.value.party_type_id,
      )?.internal_id === '91-a'
    );
  }

  ngOnInit(): void {
    this.companySearch$
      .pipe(takeUntil(this.destroy), debounceTime(500))
      .subscribe(async (name) => {
        try {
          this.autocompleteOptions =
            await this.companySearchService.getCompanySearchAutocompleteItems(
              name,
            );
        } catch (error) {
          console.error('Error while searching representative', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.companySearch$.complete();
  }

  onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.companySearch$.next(value);
  }

  isAiFilled(control: AbstractControl | null | undefined): boolean {
    return this.editCaseDialogService.isAiFilled(control);
  }

  async companySelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const item = event.option.value as CompanySearchAutocompleteItem;
    await this.editCaseDialogService.applyCompanySearchToRepresentative(
      this.representative,
      item,
    );
  }

  readonly representativeTypeDisplayWith = (id?: number): string => {
    if (!id) {
      return '';
    } else {
      return this.representativeTypes.find((t) => t.id === id)?.label ?? '';
    }
  };

  readonly partyTypeDisplayWith = (id?: number | null): string => {
    if (!id) {
      return '';
    }

    return this.partyTypes.find((partyType) => partyType.id === id)?.label ?? '';
  };

  readonly registrationOfficeDisplayWith = (id?: number | null): string => {
    if (!id) {
      return '';
    }

    const office = this.editCaseDialogService.registrationOffices.find(
      (item) => Number(item.id) === Number(id),
    );

    return office?.label || office?.name || '';
  };

  removeRep(): void {
    const representatives = this.representative.parent as FormArray<
      FormGroup<RepresentativeControls>
    >;
    const entry = Object.entries(representatives.controls).findIndex(
      (e) => e[1] === this.representative,
    );
    if (entry !== -1) {
      representatives.removeAt(entry);
    }
  }

  addChildRep(): FormGroup<RepresentativeControls> | undefined {
    if (!this.representative) {
      return;
    }

    return this.editCaseDialogService.addRepresentative(this.children);
  }

}
